import classNames from 'classnames'
import { useState, type ReactNode } from 'react'
import { InfoIcon } from '../icons/InfoIcon'
import { Modal } from './Modal'
import { Label } from './Label'
import { Card } from './Card'
import styles from './Table.module.css'

interface TableProps {
  children: ReactNode
}

interface TableHeadProps {
  children: ReactNode
}

interface TableBodyProps {
  children: ReactNode
}

interface TableRowProps {
  children: ReactNode
  onClick?: () => void
}

interface TableHeaderCellProps {
  children: ReactNode
  sortDir?: 'asc' | 'desc' | null
  onSort?: () => void
  info?: ReactNode
  infoTitle?: string
}

interface TableCellProps {
  children: ReactNode
  variant?: 'strong' | 'muted'
}

function TableHead({ children }: TableHeadProps) {
  return (
    <thead className={styles['tableHead']}>
      <tr>{children}</tr>
    </thead>
  )
}

function TableBody({ children }: TableBodyProps) {
  return <tbody>{children}</tbody>
}

function TableRow({ children, onClick }: TableRowProps) {
  return (
    <tr
      className={classNames(styles['tableRow'], onClick && styles['tableRowInteractive'])}
      onClick={onClick}
    >
      {children}
    </tr>
  )
}

function TableHeaderCell({ children, sortDir, onSort, info, infoTitle }: TableHeaderCellProps) {
  const [infoOpen, setInfoOpen] = useState(false)

  if (!onSort) {
    return (
      <th className={styles['tableHeaderCell']}>
        <Label info={info} infoTitle={infoTitle}>
          {children}
        </Label>
      </th>
    )
  }
  return (
    <th className={styles['tableHeaderCell']}>
      <div className={styles['headerCellInner']}>
        <button type="button" className={styles['sortButton']} onClick={onSort}>
          {children}
          <span className={styles['sortIcon']} data-active={String(sortDir != null)} aria-hidden>
            {sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : '↕'}
          </span>
        </button>
        {info && (
          <>
            <button
              type="button"
              className={styles['infoButton']}
              onClick={() => setInfoOpen(true)}
              aria-label="Show info"
            >
              <InfoIcon className={styles['infoIcon']} />
            </button>
            <Modal open={infoOpen} title={infoTitle ?? 'Info'} onClose={() => setInfoOpen(false)}>
              {info}
            </Modal>
          </>
        )}
      </div>
    </th>
  )
}

function TableCell({ children, variant }: TableCellProps) {
  const cls = [
    styles['tableCell'],
    variant === 'strong' ? styles['tableCellStrong'] : '',
    variant === 'muted' ? styles['tableCellMuted'] : '',
  ]
    .filter(Boolean)
    .join(' ')
  return <td className={cls}>{children}</td>
}

function Table({ children }: TableProps) {
  return (
    <Card className={styles['tableCard']}>
      <div className={styles['tableWrap']}>
        <table className={styles['table']}>{children}</table>
      </div>
    </Card>
  )
}

Table.Head = TableHead
Table.Body = TableBody
Table.Row = TableRow
Table.HeaderCell = TableHeaderCell
Table.Cell = TableCell

export { Table }
